import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { sendEvent } from "../../service";

/* ------------------------------------------------------------------ *
 * CONFIG - the only lines you may need to change
 * ------------------------------------------------------------------ */
const INPLAY_EVENT = "GET_INPLAY_MATCHES";
const POLL_MS = 1000;

// Where your socket listener stores the GET_INPLAY_MATCHES response in Redux.
// CHANGE `inPlayMatches` to your real reducer key.
const selectInPlaySocket = (state) => state.inPlayLive;
/* ------------------------------------------------------------------ */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Match Odds status. Suspend and ball running both close the price.
export function oddsHoldText(status) {
  const value = String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
  if (value === "BALL RUNNING" || value === "BALLRUNNING" || value === "BALL_RUNNING") {
    return "Ball Running";
  }
  if (value === "SUSPEND" || value === "SUSPENDED" || value.includes("SUSPEND")) {
    return "Suspended";
  }
  return "";
}
const size = (v) => (v === null || v === undefined ? "" : v);

function runnersFromFlat(m) {
  const [team1 = "", team2 = ""] = String(m.eventName || "").split(/\s+v\s+/i);
  const mk = (idx, nat, priceSlot, sizeSlot) => ({
    sId: `${m.marketId}_${idx}`,          // synthetic, see warning below
    sortPriority: idx,
    nat,
    b1: num(m[`back${priceSlot}`]),
    l1: num(m[`lay${priceSlot}`]),
    bs1: size(m[`backSize${sizeSlot}`]),
    ls1: size(m[`laySize${sizeSlot}`]),
    status: "ACTIVE",
  });
  const threeWay = m.type === "soccer" || Number(m.RunnersNumber) > 2;
  return threeWay
    ? [mk(1, team1, 1, 1), mk(2, team2, 3, 3), mk(3, "The Draw", 2, 2)]
    : [mk(1, team1, 1, 1), mk(2, team2, 3, 2)]; // cricket/tennis: price in slot 3, size in slot 2
}

// Accepts either an array of matches, or { cricket: [], soccer: [], tennis: [] }
// (optionally wrapped in `.data`). Returns a flat list with a `type` on each match.
function normalise(payload) {
  const out = [];
  if (!payload) return out;
  const push = (m, type) => {
    if (m && m.gameId != null && m.marketId != null) {
      out.push({ ...m, type: m.type || type });
    }
  };
  const root = payload.data ?? payload;
  if (Array.isArray(root)) {
    root.forEach((m) => push(m));
  } else if (typeof root === "object") {
    Object.keys(root).forEach((k) => {
      if (Array.isArray(root[k])) root[k].forEach((m) => push(m, k));
    });
  }
  return out;
}

export const liveKey = (item) => `${item?.gameId}_${item?.marketId}`;

/**
 * Polls GET_INPLAY_MATCHES while `enabled` (In-Play tab) and exposes the latest
 * odds keyed by `${gameId}_${marketId}`.
 *
 * A match that is not in the latest in-play payload is NOT in `liveOdds`, so it
 * cannot be bet on. That is what restricts betting to in-play matches.
 */
export default function useInPlayOdds(enabled) {
  const socketPayload = useSelector(selectInPlaySocket);
  const [liveOdds, setLiveOdds] = useState({});
  const [oddsFlash, setOddsFlash] = useState({});
  const liveRef = useRef({});
  const flashTimers = useRef([]);

  // 1) request in-play odds
  useEffect(() => {
    if (!enabled) return undefined;
    const pull = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      sendEvent(INPLAY_EVENT, { domain: window.location.hostname });
    };
    pull();
    const timer = setInterval(pull, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled]);

  // 2) leaving the tab clears everything
  useEffect(() => {
    if (enabled) return;
    liveRef.current = {};
    setLiveOdds({});
    setOddsFlash({});
  }, [enabled]);

  // 3) turn each socket response into liveOdds + flash keys
  useEffect(() => {
    if (!enabled) return;
    const matches = normalise(socketPayload);
    if (!matches.length) {
      liveRef.current = {};
      setLiveOdds({});
      return;
    }

    const next = {};
    const flashKeys = [];
    matches.forEach((m) => {
      const key = liveKey(m);
      const prev = liveRef.current[key];
      const incoming = Array.isArray(m.t1) && m.t1.length ? m.t1 : null;
      const prevBook = prev && Array.isArray(prev.t1) && prev.t1.length ? prev.t1 : null;
      const t1 = incoming || prevBook || runnersFromFlat(m);
      if (prev && prev.t1.length) {
        t1.forEach((runner, idx) => {
          const old =
            prev.t1.find((r) => r?.sId != null && r.sId === runner?.sId) || prev.t1[idx];
          if (!old) return;
          const id = `${key}-${runner?.sId ?? idx}`;
          if (Number(old.b1) !== Number(runner?.b1)) flashKeys.push(`${id}:back`);
          if (Number(old.l1) !== Number(runner?.l1)) flashKeys.push(`${id}:lay`);
        });
      }
      next[key] = {
        _id: m._id,
        type: m.type,
        gameId: m.gameId,
        marketId: m.marketId,
        t1,
        suspended: m?.suspend?.odds === true || m?.oddsSuspended === true,
        oddsLimit: m.oddsLimit || null,
        bet_odds_limit: m.bet_odds_limit || null,
      };
    });

    // replace wholesale: matches that left the in-play list disappear
    liveRef.current = next;
    setLiveOdds(next);

    if (!flashKeys.length) return;
    setOddsFlash((prev) => {
      const n = { ...prev };
      flashKeys.forEach((k) => delete n[k]);
      return n;
    });
    flashTimers.current.push(
      setTimeout(() => {
        setOddsFlash((prev) => {
          const n = { ...prev };
          flashKeys.forEach((k) => {
            n[k] = true;
          });
          return n;
        });
      }, 30),
      setTimeout(() => {
        setOddsFlash((prev) => {
          const n = { ...prev };
          flashKeys.forEach((k) => delete n[k]);
          return n;
        });
      }, 700)
    );
  }, [socketPayload, enabled]);

  useEffect(
    () => () => {
      flashTimers.current.forEach(clearTimeout);
    },
    []
  );

  const getLiveMatch = (key) => liveRef.current[key];

  return { liveOdds, oddsFlash, getLiveMatch };
}
